import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/cn'
import { type AppShotId, appShots } from '../appShots'

/*
 * ─── ONE MEASUREMENT, AND TWO WAYS OF ARRIVING AT IT ────────────────────────
 *
 * An iPhone Pro Max screen is 440 × 956 points — ratio 2.17. Every phone on
 * this page is that shape, but the files come from two different places and
 * only one of them is already the right size.
 *
 *   • A HANDSET capture (`deviceChrome: true` in the manifest) is the whole
 *     screen: 739 × 1600, ratio 2.17, its own clock and battery baked in.
 *     Nothing is added around it.
 *
 *   • A SCRIPT capture from `scripts/shoot-app.mjs` is 440 × 881, ratio 2.00 —
 *     the content BETWEEN the two strips, because headless Chrome draws no
 *     status bar and react-native-web has no safe-area inset to leave room for
 *     one. The strips are drawn below, and the sum is the same 956.
 *
 *         status bar   54 / 440 = 12.27 %
 *         content     881            ← the image
 *         home area    21 / 440 =  4.77 %
 *                     ───
 *                     956            = 2.17 × 440
 *
 * Two mistakes have already been made here, in opposite directions, and both
 * are cheap to make again:
 *
 *   1. Capturing the FULL height (2.24) and then adding the strips anyway.
 *      Every phone came out visibly long.
 *   2. Drawing the strips over a handset capture. The frame then has two
 *      clocks an inch apart — 9:41 in the bezel, 10:51 in the picture.
 *
 * Which is why `deviceChrome` exists and why it is not optional to think
 * about when a file changes.
 */
const SHOT_WIDTH = 440
const SHOT_HEIGHT = 881
const STATUS_BAR = '12.27%'
const HOME_INDICATOR = '4.77%'

type AppShotProps = {
  id: AppShotId
  className?: string
  /**
   * Above the fold. Loads the image eagerly rather than lazily — the hero's
   * shots are the LCP candidate on a wide screen, and `loading="lazy"` on
   * something already in the viewport costs a round trip for nothing.
   */
  priority?: boolean
}

/**
 * ONE SWAPPABLE SLOT, USED EVERYWHERE.
 *
 * The client's reference PDF illustrates nearly every homepage section with a
 * phone. We have one real screenshot; the developer is capturing the rest. The
 * two bad answers were (a) reference image files that do not exist, which is a
 * broken `<img>` in front of the client, and (b) rebuilding each app screen in
 * CSS, which is a day of work thrown away the moment the real files land.
 *
 * So this draws the frame and fills it with whichever it has:
 *
 *   • a real file  → `<img>`, lazy, with the box reserved;
 *   • no file yet  → a plain panel of the SAME box, naming the section.
 *
 * The placeholder is deliberately quiet — a muted fill, a dashed rule and two
 * lines of text. It reads as "image coming", not as a failed load, and it costs
 * nothing: no icon import, no SVG, no per-section artwork. That last point is
 * also what protects the homepage's bundle headroom.
 *
 * The frame, the bezel and the glow are CSS, not part of the image. A
 * screenshot of a rendered phone frame is soft on a retina display and carries
 * its own drop shadow into whatever background it lands on, whereas a border
 * and a `box-shadow` stay sharp at any size and follow the theme.
 *
 * ⚠️ WHAT TO CHANGE WHEN A SCREENSHOT ARRIVES: `../appShots.ts`, one field.
 * Nothing in this file, and nothing in any section.
 */
export const AppShot = ({ id, className, priority = false }: AppShotProps) => {
  const { t, i18n } = useTranslation()

  const shot = appShots.find((candidate) => candidate.id === id)
  if (!shot) return null

  const label = t(shot.labelKey)

  /*
   * THE SCREENSHOT FOLLOWS THE PAGE'S LANGUAGE WHEN THERE IS A VERSION OF IT.
   *
   * `fileNb` is optional and most slots will not have one for a while, so this
   * falls back rather than blanking. `startsWith` because `load: 'languageOnly'`
   * can still leave a region on the detected value before it normalises.
   */
  const nbFile = 'fileNb' in shot ? shot.fileNb : undefined
  const file = (i18n.language.startsWith('nb') ? (nbFile ?? shot.file) : shot.file) ?? null

  /*
   * Does this file bring its own status bar? See the header — getting it wrong
   * is two clocks, or a web page in a phone outline. The waiting panel counts
   * as a script capture, because it is drawn at the script's content ratio.
   */
  const deviceChrome = 'deviceChrome' in shot ? shot.deviceChrome === true : false
  const drawChrome = file !== null ? !deviceChrome : true

  return (
    <div
      style={{ containerType: 'inline-size' }}
      className={cn(
        'relative mx-auto w-[12rem] sm:w-[14rem] lg:w-full lg:max-w-[16rem]',
        className,
      )}
    >
      {/* Brand glow behind the device. Decorative, and sized to stay inside the
          column so it can never widen the grid on a small screen. */}
      <div
        aria-hidden="true"
        className="bg-primary/20 pointer-events-none absolute inset-x-4 top-10 -z-10 h-3/4 rounded-full blur-3xl"
      />

      {/*
        ─── EVERY DIMENSION IS `cqw`, AND THAT IS THE WHOLE TRICK ─────────────

        A phone's bezel, corner radius and Dynamic Island are fixed fractions of
        its width. This renders at three sizes (12rem, 14rem, up to 16rem), so
        anything expressed in `rem` is right at one of them and wrong at the
        other two — the previous 0.6rem band was 3.8 % of the width at 16rem and
        5 % at 12rem, which is roughly twice a real titanium edge and made the
        device read as a cheap mockup.

        `cqw` is 1 % of the container's width, so the proportions below hold at
        every size. Taken from a Pro Max screen of 440 pt:
        band ≈ 1.6 %, screen radius 55/440 = 12.5 %, Dynamic Island
        125 × 37 pt = 28.4 % × 8.4 %.
      */}
      <div
        style={{ padding: '1.6cqw', borderRadius: '14.1cqw' }}
        className="shadow-3 bg-neutral-900 dark:bg-neutral-700"
      >
        <div style={{ borderRadius: '12.5cqw' }} className="relative overflow-hidden bg-white">
          {/*
          ─── THE STATUS BAR AND THE HOME INDICATOR ARE CSS, LIKE THE BEZEL ───

          A phone screenshot normally carries the carrier, wifi and battery
          glyphs, and without them a picture inside a phone frame reads as a
          web page in a phone frame. The captures from
          `scripts/shoot-app.mjs` have none: headless Chrome draws no status
          bar, and the app itself starts at y = 0 on web because
          react-native-web has no safe-area inset to leave room.

          Drawing it here rather than into each image means it stays sharp at
          any density, follows the frame if the bezel changes, and costs no
          bytes — and every future capture gets it without being re-taken.

          It is `aria-hidden` throughout. The time is not read out, the glyphs
          are not content, and the alt text on the image already describes the
          screen.
        */}
          {drawChrome ? (
            <div
              aria-hidden="true"
              style={{ paddingTop: STATUS_BAR }}
              className="relative w-full bg-white"
            >
              <span
                className="absolute inset-0 flex items-center justify-between"
                style={{ padding: '0 7.5cqw' }}
              >
                <span
                  style={{ fontSize: '3.6cqw' }}
                  className="font-semibold leading-none tracking-tight text-neutral-900"
                >
                  9:41
                </span>
                <span className="flex items-center" style={{ gap: '1.4cqw' }}>
                  {/* Signal and battery as plain shapes — no icon import, because
                  this must not add a byte to the homepage's JS budget. */}
                  <span className="flex items-end" style={{ gap: '0.35cqw' }}>
                    {[1.1, 1.6, 2.1, 2.6].map((h) => (
                      <span
                        key={h}
                        style={{ height: `${h}cqw`, width: '0.7cqw', borderRadius: '0.2cqw' }}
                        className="bg-neutral-900"
                      />
                    ))}
                  </span>
                  <span
                    style={{
                      height: '2.5cqw',
                      width: '4.4cqw',
                      borderRadius: '0.8cqw',
                      borderWidth: '0.35cqw',
                      padding: '0.35cqw',
                    }}
                    className="border-neutral-900"
                  >
                    <span
                      style={{ borderRadius: '0.3cqw' }}
                      className="block h-full w-2/3 bg-neutral-900"
                    />
                  </span>
                </span>
              </span>
            </div>
          ) : null}

          {/*
          THE DYNAMIC ISLAND IS DRAWN FOR BOTH KINDS OF FILE, AND IT IS THE ONE
          PIECE THAT DOES NOT DOUBLE UP.

          It is the single thing that makes a frame read as a current iPhone
          rather than a generic rounded rectangle, and it was missing from the
          first attempt. On a handset capture it is not in the picture either —
          it is a hole in the glass, so the screenshot shows whatever the app
          painted behind it. Drawing it over the top is therefore correct in
          both cases.

          125 × 37 pt on a 440 pt screen, about 11 pt down: 28.4 % × 8.4 % of
          the width. It spans 35.8–64.2 % horizontally, and the clock ends
          around 20 % with the battery starting around 78 %, so it covers
          neither — including the real ones in a handset capture.
        */}
          <span
            aria-hidden="true"
            style={{
              width: '28.4cqw',
              height: '8.4cqw',
              borderRadius: '4.2cqw',
              top: '2.5cqw',
            }}
            className="absolute left-1/2 z-10 -translate-x-1/2 bg-neutral-950"
          />

          {file === null ? (
            /*
            The waiting state. `aspect-[440/881]` is the same box the image
            will occupy, so dropping the file in changes nothing but the
            contents. `aria-hidden` because it is scaffolding, not content —
            a screen reader should not be told about a missing asset.
          */
            <div
              aria-hidden="true"
              className="bg-muted flex aspect-[440/881] w-full flex-col items-center justify-center gap-1.5 px-5 text-center"
            >
              <span className="border-input w-full rounded-lg border border-dashed px-3 py-6">
                <span className="type-overline text-muted-foreground block">
                  {t('home.shot.pending')}
                </span>
                <span className="type-body-sm text-foreground mt-1.5 block font-semibold">
                  {label}
                </span>
              </span>
            </div>
          ) : (
            <img
              src={`/${file}`}
              width={SHOT_WIDTH}
              height={deviceChrome ? 956 : SHOT_HEIGHT}
              alt={'altKey' in shot ? t(shot.altKey) : t('home.shot.alt', { label })}
              className="block w-full"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
            />
          )}

          {/* The home indicator. Same reasoning and the same sizing rule — and
            the same reason it is skipped for a handset capture, which already
            has the 21 pt of screen below the tab bar. */}
          {drawChrome ? (
            <div
              aria-hidden="true"
              style={{ paddingTop: HOME_INDICATOR }}
              className="relative w-full bg-white"
            >
              <span className="absolute inset-0 flex items-center justify-center">
                <span
                  style={{ height: '1cqw', width: '34%', borderRadius: '0.5cqw' }}
                  className="bg-neutral-900/85"
                />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
